'use es6';

import { trackCreateEmailInteraction, trackDashboardInteraction } from 'EmailComponents/utils/tracking';
import { RequestStatus } from 'EmailData/constants/requestStatus';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import PortalIdParser from 'PortalIdParser';
import { Link } from 'foundations-components/transitional/components/Link';
import { DEFAULT_VIEW_PATH } from 'email-management-lib/Shared/constants/frameworkTable';
import { CREATE_AN_EMAIL_STATE, FILTER, NO_ARCHIVED_EMAIL, NO_DRAFT_EMAIL, NO_MATCH_FILTER, NO_MATCH_SEARCH, NO_SCHEDULED_EMAIL, NO_SENT_EMAIL, SEARCH } from 'email-management-lib/Shared/constants/manage';
import DashboardEmptyState from 'email-management-lib/refactor/components/manage/DashboardEmptyState';
import { View } from 'foundations-components/components/View';
import { ErrorMessage } from 'foundations-components/transitional/components/ErrorMessage';
import PropTypes from 'prop-types';
import { NavMarker } from 'react-rhumb';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { Illustration } from 'foundations-components/transitional/components/Illustration';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const {
  FAILED,
  PENDING
} = RequestStatus;
export const EMPTY_STATE_TYPES = {
  MAYBE_ARCHIVED: 'MAYBE_ARCHIVED',
  NO_EMAIL: 'NO_EMAIL',
  NO_ARCHIVED: 'NO_ARCHIVED'
};
const EmptyStateRow = props => {
  const getEmptyStateType = () => {
    const {
      portalHasEmails,
      hasArchivedEmails
    } = props;
    if (!portalHasEmails && !hasArchivedEmails) {
      return EMPTY_STATE_TYPES.NO_EMAIL;
    } else {
      return EMPTY_STATE_TYPES.MAYBE_ARCHIVED;
    }
  };
  const getEmptyStateTypeInList = () => {
    const {
      selectedViewPath,
      isSearchOrFilterOnTop
    } = props;
    if (isSearchOrFilterOnTop === SEARCH) {
      return NO_MATCH_SEARCH;
    } else if (isSearchOrFilterOnTop === FILTER) {
      return NO_MATCH_FILTER;
    }
    switch (selectedViewPath) {
      case DEFAULT_VIEW_PATH.ALL:
        return CREATE_AN_EMAIL_STATE;
      case DEFAULT_VIEW_PATH.DRAFT:
        return NO_DRAFT_EMAIL;
      case DEFAULT_VIEW_PATH.SCHEDULED:
        return NO_SCHEDULED_EMAIL;
      case DEFAULT_VIEW_PATH.SENT:
        return NO_SENT_EMAIL;
      case DEFAULT_VIEW_PATH.ARCHIVED:
        return NO_ARCHIVED_EMAIL;
      default:
        return CREATE_AN_EMAIL_STATE;
    }
  };
  const renderEmptyStateMessage = emptyStateType => {
    const {
      shouldDisableCreateEmailAction
    } = props;
    switch (emptyStateType) {
      case EMPTY_STATE_TYPES.NO_EMAIL:
        return /*#__PURE__*/_jsx(FormattedJSXMessage, {
          elements: {
            CreateLink: ({
              children
            }) => /*#__PURE__*/_jsx(Link, {
              disabled: shouldDisableCreateEmailAction,
              href: `/content/${PortalIdParser.get()}/create/email`,
              onClick: () => {
                trackDashboardInteraction({
                  otherActions: 'create-email-from-empty-state-link'
                });
                trackCreateEmailInteraction();
              },
              children: children
            })
          },
          message: "email-management.emptyState.noEmailInPortalMessage_jsx"
        });
      case EMPTY_STATE_TYPES.NO_ARCHIVED:
      case EMPTY_STATE_TYPES.MAYBE_ARCHIVED:
        return /*#__PURE__*/_jsx(FormattedMessage, {
          message: "email-management.emptyState.folderMessage"
        });
      default:
        return '';
    }
  };
  const renderEmptyStateMessageInList = emptyStateType => {
    const {
      hasUsersAndTeamsAccess
    } = props;
    if (emptyStateType === NO_MATCH_FILTER || emptyStateType === NO_MATCH_SEARCH) {
      return `email-management.emptyState.${emptyStateType}${hasUsersAndTeamsAccess ? 'TeamAccess' : ''}`;
    } else {
      return `email-management.emptyState.${emptyStateType}`;
    }
  };
  const renderEmptyStateRow = () => {
    const {
      fetchStatus,
      isSearchOrFilterOnTop,
      listMode,
      onClearEmailSearch,
      onClearAllEmailFilters
    } = props;
    const emptyStateType = listMode === 'list' ? getEmptyStateTypeInList() : getEmptyStateType();
    switch (fetchStatus) {
      case FAILED:
        return /*#__PURE__*/_jsx(NavMarker, {
          name: "TABLE_ERROR_STATE",
          children: /*#__PURE__*/_jsx(ErrorMessage, {
            children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
              message: "email-management.errors.generic"
            })
          })
        });
      case PENDING:
        return /*#__PURE__*/_jsx(View, {
          blockSize: "400px",
          "data-test-id": "empty-state-row-loading-state"
        });
      default:
        return /*#__PURE__*/_jsx(NavMarker, {
          name: "TABLE_EMPTY_STATE",
          children: listMode !== 'list' ? /*#__PURE__*/_jsxs(Flex, {
            alignItems: "center",
            flexDirection: "column",
            children: [/*#__PURE__*/_jsx(Illustration, {
              "data-test-id": "empty-state-illustration",
              marginBlockEnd: "500",
              name: "empty-state-charts",
              width: "300px"
            }), /*#__PURE__*/_jsx(Text, {
              "data-test-id": "empty-state-text",
              use: "help",
              children: renderEmptyStateMessage(emptyStateType)
            })]
          }) : /*#__PURE__*/_jsx(DashboardEmptyState, {
            id: `${emptyStateType}_${listMode}`,
            isSearchOrFilterOnTop: isSearchOrFilterOnTop,
            message: renderEmptyStateMessageInList(emptyStateType),
            onClearAllEmailFilters: onClearAllEmailFilters,
            onClearEmailSearch: onClearEmailSearch
          })
        });
    }
  };
  return props.listMode === 'list' ? /*#__PURE__*/_jsx(View, {
    cursor: "default",
    children: renderEmptyStateRow()
  }) : /*#__PURE__*/_jsx(View, {
    as: "tr",
    cursor: "default",
    children: props.selectedColumns.map(column => {
      if (column === 'name') {
        return /*#__PURE__*/_jsx("td", {
          children: renderEmptyStateRow()
        }, column);
      }
      return /*#__PURE__*/_jsx("td", {}, column);
    })
  });
};
EmptyStateRow.propTypes = {
  fetchStatus: PropTypes.string.isRequired,
  hasArchivedEmails: PropTypes.bool.isRequired,
  hasUsersAndTeamsAccess: PropTypes.bool.isRequired,
  isSearchOrFilterOnTop: PropTypes.string.isRequired,
  listMode: PropTypes.string.isRequired,
  onClearAllEmailFilters: PropTypes.func.isRequired,
  onClearEmailSearch: PropTypes.func.isRequired,
  portalHasEmails: PropTypes.bool.isRequired,
  selectedColumns: PropTypes.object,
  selectedViewPath: PropTypes.string.isRequired,
  shouldDisableCreateEmailAction: PropTypes.bool.isRequired
};
export default EmptyStateRow;